import React from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AccessProvider } from "./context/AccessContext";
import { ThemeProvider } from "./context/ThemeContext";
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import DeleteAccountPage from "./pages/DeleteAccount";
// PhoneAuth import intentionally removed — phone-OTP UI is feature-flagged
// off as of Feb 2026. The file remains at /app/frontend/src/pages/PhoneAuth.jsx
// so the flow can be revived later by restoring this import + the route
// below AND flipping OTP_AUTH_ENABLED=true on the backend.
import GoogleCallback from "./pages/GoogleCallback";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Dashboard from "./pages/Dashboard";
import Syllabus from "./pages/Syllabus";
import Timer from "./pages/Timer";
import Reports from "./pages/Reports";
import MockTests from "./pages/MockTests";
import Friends from "./pages/Friends";
import Messages from "./pages/Messages";
import SylloxAI from "./pages/SylloxAI";
import Profile from "./pages/Profile";
import Premium from "./pages/Premium";
import Settings from "./pages/Settings";
import Leaderboard from "./pages/Leaderboard";
import Calendar from "./pages/Calendar";
import PublicProfile from "./pages/PublicProfile";
import RouteAnalytics from "./components/RouteAnalytics";
import AdminRoute from "./components/AdminRoute";
import AdminDashboard from "./pages/AdminDashboard";

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<RedirectIfAuthed><Login /></RedirectIfAuthed>} />
      <Route path="/signup" element={<RedirectIfAuthed><Signup /></RedirectIfAuthed>} />
      <Route path="/register" element={<Navigate to="/signup" replace />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* Public account deletion — Google Play Console external URL.
          Intentionally accessible WITHOUT auth: users may need to delete
          their account after uninstalling the app or losing device access. */}
      <Route path="/delete-account" element={<DeleteAccountPage />} />
      {/* Legacy phone-auth path redirects to /login. Bookmarks, old emails,
          and old SMS deeplinks keep working — they just land on Google sign-in. */}
      <Route path="/auth/phone" element={<Navigate to="/login" replace />} />
      <Route path="/auth/google/callback" element={<GoogleCallback />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/dashboard" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
      <Route path="/syllabus" element={<ProtectedRoute><Layout><Syllabus /></Layout></ProtectedRoute>} />
      <Route path="/timer" element={<ProtectedRoute><Layout><Timer /></Layout></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><Layout><Reports /></Layout></ProtectedRoute>} />
      <Route path="/mock-tests" element={<ProtectedRoute><Layout><MockTests /></Layout></ProtectedRoute>} />
      <Route path="/community" element={<ProtectedRoute><Layout><Friends /></Layout></ProtectedRoute>} />
      <Route path="/leaderboard" element={<ProtectedRoute><Layout><Leaderboard /></Layout></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><Layout><Calendar /></Layout></ProtectedRoute>} />
      <Route path="/u/:username" element={<ProtectedRoute><Layout><PublicProfile /></Layout></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><Layout><Messages /></Layout></ProtectedRoute>} />
      <Route path="/messages/:friendId" element={<ProtectedRoute><Layout><Messages /></Layout></ProtectedRoute>} />
      <Route path="/ai" element={<ProtectedRoute><Layout><SylloxAI /></Layout></ProtectedRoute>} />
      <Route path="/premium" element={<ProtectedRoute><Layout><Premium /></Layout></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Layout><Profile /></Layout></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Layout><Settings /></Layout></ProtectedRoute>} />
      <Route path="/admin" element={<AdminRoute><Layout><AdminDashboard /></Layout></AdminRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function RedirectIfAuthed({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

export default function App() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // The Google Client ID is read at build time from REACT_APP_GOOGLE_CLIENT_ID.
  // We render the rest of the tree even if it's missing so the existing
  // email/pw + phone-OTP flows keep working — the Google button just no-ops.
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
  return (
    <ThemeProvider>
      <GoogleOAuthProvider clientId={googleClientId}>
        <BrowserRouter>
          <AuthProvider>
            <AccessProvider>
              <RouteAnalytics />
              <AppRouter />
              <Toaster position="top-right" richColors />
            </AccessProvider>
          </AuthProvider>
        </BrowserRouter>
      </GoogleOAuthProvider>
    </ThemeProvider>
  );
}
